/**
 * Schallsignale als Zeitleiste.
 *
 * Regel 32 legt die Längen fest: ein kurzer Ton dauert etwa eine Sekunde, ein langer
 * vier bis sechs. Damit ist jedes Signal aus Teil D eine Folge von Tönen und Pausen,
 * und der Browser kann es erzeugen, ohne dass eine einzige Audiodatei ausgeliefert wird.
 */
import type { SignalData } from './schema'

/** Längen in Sekunden, aus Regel 32(b) und (c). */
export const SHORT_S = 1
export const PROLONGED_S = 5
export const GAP_S = 1
/** Regel 35(g): etwa fünf Sekunden schnelles Läuten. */
export const BELL_RAPID_S = 5
/** Anhang IV, Abschnitt 1(b): ein Dauerton, hier acht Sekunden als Hörprobe. */
export const CONTINUOUS_S = 8
export const STROKE_S = 0.4
export const STROKE_GAP_S = 0.8

export interface SignalStep {
  kind: 'short' | 'prolonged' | 'continuous' | 'bell' | 'gong' | 'silence'
  /** Dauer in Sekunden. */
  duration: number
}

/** Die Zeitleiste eines Signals, ohne die Wiederholung. */
export function timeline(signal: SignalData): SignalStep[] {
  const steps: SignalStep[] = []
  for (const [index, element] of signal.pattern.entries()) {
    if (index > 0) steps.push({ kind: 'silence', duration: GAP_S })
    for (let i = 0; i < element.count; i += 1) {
      if (i > 0) steps.push({ kind: 'silence', duration: GAP_S })
      switch (element.kind) {
        case 'short':
          steps.push({ kind: 'short', duration: SHORT_S })
          break
        case 'prolonged':
          steps.push({ kind: 'prolonged', duration: PROLONGED_S })
          break
        case 'continuous':
          steps.push({ kind: 'continuous', duration: CONTINUOUS_S })
          break
        case 'bell-rapid':
          steps.push({ kind: 'bell', duration: BELL_RAPID_S })
          break
        case 'gong-rapid':
          steps.push({ kind: 'gong', duration: BELL_RAPID_S })
          break
        case 'bell-strokes':
          steps.push({ kind: 'bell', duration: STROKE_S })
          if (i < element.count - 1) steps.push({ kind: 'silence', duration: STROKE_GAP_S - GAP_S })
          break
      }
    }
  }
  return steps
}

export function durationS(signal: SignalData): number {
  return timeline(signal).reduce((n, step) => n + step.duration, 0)
}

/** Zahl der Töne einer Art, die Zahl also, die der Lernende hört und zählt. */
export function countOf(signal: SignalData, kind: 'short' | 'prolonged'): number {
  return signal.pattern.filter((p) => p.kind === kind).reduce((n, p) => n + p.count, 0)
}

/** Zahlwörter, mit denen die Regeln die Töne zählen. */
const NUMBER_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5 }

export interface BlastSpec {
  count: number
  kind: 'short' | 'prolonged'
}

/** Zahlwort für eine Zahl, für die Suche im Regeltext. */
function numberWord(count: number): string | undefined {
  return Object.entries(NUMBER_WORDS).find(([, value]) => value === count)?.[0]
}

/**
 * Die Tonangaben eines Regeltextes in der Reihenfolge, in der sie dastehen.
 * "two prolonged blasts followed by one short blast" ergibt zwei Angaben.
 *
 * Ausgenommen sind Lichtsignale: Regel 34(d) erlaubt, die fünf kurzen Töne durch fünf
 * kurze Blitze zu ergänzen, und die sind keine Töne.
 */
export function blastSpecs(text: string): BlastSpec[] {
  const spec = /\b(one|two|three|four|five)\s+(short|prolonged)(?!\s+(?:and\s+rapid\s+)?flash)/gi
  return [...text.matchAll(spec)].map((m) => ({
    count: NUMBER_WORDS[m[1]!.toLowerCase()]!,
    kind: m[2]!.toLowerCase() as BlastSpec['kind'],
  }))
}

/** Dasselbe aus dem Muster eines Signals: nur Pfeifentöne, Glocke und Gong zählen hier nicht. */
export function patternSpecs(signal: SignalData): BlastSpec[] {
  return signal.pattern
    .filter((p) => p.kind === 'short' || p.kind === 'prolonged')
    .map((p) => ({ count: p.count, kind: p.kind as BlastSpec['kind'] }))
}

function sameSpecs(a: BlastSpec[], b: BlastSpec[]): boolean {
  return a.length === b.length && a.every((x, i) => x.count === b[i]!.count && x.kind === b[i]!.kind)
}

export type BlastCheck =
  | { ok: true }
  | { ok: false; reason: 'folge'; found: BlastSpec[]; want: BlastSpec[] }
  | { ok: false; reason: 'ankerFehlt'; anchor: string }
  | { ok: false; reason: 'ankerFolge'; found: BlastSpec[]; want: BlastSpec[]; anchor: string }

/**
 * Deckt der zitierte Regeltext genau diese Tonfolge?
 *
 * Zwei Fälle. Nennt der Absatz nur ein Signal, muss seine Tonfolge die des Eintrags sein.
 * Nennt er mehrere nebeneinander (Regel 34(a) zählt alle drei Manöversignale in einem
 * Satz auf), reicht das nicht: dann muss die Bedeutung, die die App anzeigt, im Regeltext
 * stehen, und die Tonangabe unmittelbar davor muss stimmen. Sonst bliebe unbemerkt, wenn
 * ein Eintrag zwei kurze Töne mit "Kurs nach Steuerbord" verbindet statt mit Backbord.
 */
export function checkBlasts(signal: SignalData, text: string): BlastCheck {
  const want = patternSpecs(signal)
  if (want.length === 0) return { ok: true }
  const all = blastSpecs(text)
  if (sameSpecs(all, want)) return { ok: true }

  const anchor = signal.meaning.en.replace(/\.$/, '')
  const at = text.toLowerCase().indexOf(anchor.toLowerCase())
  if (at === -1) return { ok: false, reason: 'ankerFehlt', anchor }
  // Das öffnende Anführungszeichen gehört zu dieser Bedeutung, jedes frühere zur vorigen.
  const before = text.slice(0, at).replace(/["“”]\s*$/, '')
  const previous = before.search(/["“”][^"“”]*$/)
  const segment = previous === -1 ? before : before.slice(previous + 1)
  const near = blastSpecs(segment)
  if (!sameSpecs(near, want)) return { ok: false, reason: 'ankerFolge', found: near, want, anchor }
  return { ok: true }
}


export type BellCheck =
  | { ok: true }
  | { ok: false; reason: 'schlaege'; count: number }
  | { ok: false; reason: 'laeuten' }
  | { ok: false; reason: 'gong' }

/**
 * Deckt der Regeltext auch Glocke und Gong?
 *
 * Für die Pfeifentöne prüft `checkBlasts` die Folge; Glocke und Gong haben keine Tonfolge,
 * aber sehr wohl eine Zahl: Regel 35(h) verlangt "three separate and distinct strokes",
 * und drei Schläge sind etwas anderes als fünf. Ohne diese Prüfung stünde die Schlagzahl
 * unbelegt in den Daten.
 */
export function checkBell(signal: SignalData, text: string): BellCheck {
  for (const element of signal.pattern) {
    if (element.kind === 'bell-strokes') {
      const word = numberWord(element.count)
      const pattern = word && new RegExp(`\\b${word}\\b[^.]{0,40}?\\bstrokes?\\b`, 'i')
      if (!pattern || !pattern.test(text)) return { ok: false, reason: 'schlaege', count: element.count }
    }
    if (element.kind === 'bell-rapid' && !/bell\s+rapidly/i.test(text)) return { ok: false, reason: 'laeuten' }
    if (element.kind === 'gong-rapid' && !/gong\b[^.]{0,60}?\brapidly\b/i.test(text)) {
      return { ok: false, reason: 'gong' }
    }
  }
  return { ok: true }
}

/** Wörter, an denen ein Name seine Tonart nennt, je Sprache. */
const KIND_WORDS: Record<string, { de: RegExp; en: RegExp }> = {
  short: { de: /kurz/i, en: /short/i },
  prolonged: { de: /lang/i, en: /prolonged/i },
  continuous: { de: /dauerton/i, en: /continuous/i },
  'bell-rapid': { de: /glocke/i, en: /bell/i },
  'bell-strokes': { de: /schläg|schlag/i, en: /stroke/i },
  'gong-rapid': { de: /gong/i, en: /gong/i },
}

const COUNT_WORDS: Record<number, { de: RegExp; en: RegExp }> = {
  2: { de: /zwei/i, en: /two/i },
  3: { de: /drei/i, en: /three/i },
  4: { de: /vier/i, en: /four/i },
  5: { de: /fünf/i, en: /five/i },
}

export type NameCheck =
  | { ok: true }
  | { ok: false; lang: 'de' | 'en'; missing: string }
  | { ok: false; lang: 'de' | 'en'; extra: number }

/**
 * Nennt der Name, was wirklich gespielt wird?
 *
 * Der Name steht in der Liste, auf dem Bildschirm und im Quiz, und er ist das, woran
 * sich der Lernende das Signal merkt. Weicht er vom Muster ab, lernt jemand etwas
 * Falsches, ohne dass eine Fundstelle daran schuld wäre. Geprüft wird die Reihenfolge:
 * "Drei Schläge, Glocke, drei Schläge" muss drei Teile in dieser Folge nennen.
 */
export function checkName(signal: SignalData): NameCheck {
  for (const lang of ['de', 'en'] as const) {
    const name = signal.name[lang]
    let at = 0
    for (const element of signal.pattern) {
      const count = COUNT_WORDS[element.count]?.[lang]
      if (element.count > 1) {
        if (!count) return { ok: false, lang, missing: `Zahlwort für ${element.count}` }
        const hit = name.slice(at).search(count)
        if (hit === -1) return { ok: false, lang, missing: `${element.count} vor ${element.kind}` }
        at += hit
      }
      const kind = KIND_WORDS[element.kind]![lang]
      const hit = name.slice(at).search(kind)
      if (hit === -1) return { ok: false, lang, missing: element.kind }
      at += hit + 1
    }
    // Der Name darf auch nicht mehr nennen, als gespielt wird: fehlt im Muster ein Teil,
    // stünde er sonst weiter im Namen und niemandem fiele es auf.
    const named = Object.values(KIND_WORDS).reduce(
      (n, words) => n + (name.match(new RegExp(words[lang].source, 'gi')) ?? []).length,
      0,
    )
    if (named !== signal.pattern.length) return { ok: false, lang, extra: named }
  }
  return { ok: true }
}

export type RepeatCheck = { ok: true } | { ok: false; seconds: number }

/**
 * Steht der Abstand der Wiederholung im zitierten Absatz? Regel 35 nennt ihn ausdrücklich
 * ("at intervals of not more than 2 minutes"), und eine App, die alle zehn Sekunden
 * wiederholt, behauptet etwas anderes als die Regel.
 */
export function checkRepeat(signal: SignalData, text: string): RepeatCheck {
  if (signal.repeatS <= 0) return { ok: true }
  const minutes = signal.repeatS / 60
  if (!Number.isInteger(minutes) || minutes < 1) return { ok: false, seconds: signal.repeatS }
  const wanted =
    minutes === 1
      ? /\b(1|one)\s+minute\b/i
      : new RegExp(`\\b${minutes === 2 ? '(2|two)' : String(minutes)}\\s+minutes\\b`, 'i')
  return wanted.test(text) ? { ok: true } : { ok: false, seconds: signal.repeatS }
}

export type ClaimCheck =
  | { ok: true }
  | { ok: false; reason: 'fremdeRegel'; found: string }
  | { ok: false; reason: 'binnen'; found: string }
  | { ok: false; reason: 'wertung'; found: string }

/**
 * Wertungen und Alleinstellungen lassen sich nicht belegen, also stehen sie nicht da.
 *
 * Diese Liste fängt Muster ab, keine Bedeutung: wer "am öftesten" statt "am häufigsten"
 * schreibt, kommt daran vorbei. Deshalb ist sie nur die eine Hälfte des Gates; die andere
 * ist die Freigabe in `data/erklaerungen-geprueft.json`, die jede geänderte Erklärung
 * sichtbar macht.
 */
const JUDGEMENTS = [
  /\beinzig/i,
  /\bhäufigst/i,
  /\bwichtigst/i,
  // Ohne Wortgrenze: \b kennt Umlaute nicht als Wortzeichen, "am öftesten" rutschte sonst durch.
  /öftest/i,
  /\bseltenst/i,
  /\bmeist(ens|e)\b/i,
  /\bam (meisten|wenigsten|besten|schlechtesten)\b/i,
  /\b(immer|nie|niemals)\b/i,
  /überhaupt/i,
  /\bwie überall\b/i,
  /\bkein anderes\b/i,
  /\bthe (only|best|worst)\b/i,
  /\bmost (common|important|frequent)\b/i,
  /\b(always|never)\b/i,
  /\btells you least\b/i,
  /\bof all\b/i,
  /\bno other\b/i,
  /\bunique\b/i,
]

/** Zahlwörter, mit denen eine Erklärung eine Regel nennen könnte, ohne Ziffer zu schreiben. */
const SPELLED = [
  ['eins|ein|one', 1],
  ['zwei|two', 2],
  ['drei|three', 3],
  ['vier|four', 4],
  ['fünf|five', 5],
  ['sechs|six', 6],
  ['sieben|seven', 7],
  ['acht|eight', 8],
  ['neun|nine', 9],
  ['zehn|ten', 10],
  ['elf|eleven', 11],
  ['zwölf|twelve', 12],
  ['dreizehn|thirteen', 13],
  ['vierzehn|fourteen', 14],
  ['fünfzehn|fifteen', 15],
  ['sechzehn|sixteen', 16],
  ['siebzehn|seventeen', 17],
  ['achtzehn|eighteen', 18],
  ['neunzehn|nineteen', 19],
  ['zwanzig|twenty', 20],
] as const

/**
 * Prüft die Erklärung auf Aussagen, die der Eintrag nicht belegen kann.
 *
 * Zwei Fälle sind dem Verifier zweimal aufgefallen: eine Erklärung nennt eine Regel, die
 * der Eintrag nicht zitiert, oder sie sagt beiläufig etwas über die US-Binnenfassung,
 * obwohl der Eintrag nur die internationale zitiert. Beides ist prüfbar.
 */
export function checkClaims(signal: SignalData, citedRules: number[]): ClaimCheck {
  // Bedeutung und Erklärung stehen beide im Bild, also gilt für beide dasselbe. Der
  // fünfte Verifier-Lauf hat gezeigt, dass eine Wertung in "meaning" sonst durchläuft.
  for (const lang of ['de', 'en'] as const) {
    const text = `${signal.meaning[lang]} ${signal.explanation[lang]}`
    for (const match of text.matchAll(/\b(?:Regel|Rule)\s+(\d+)/gi)) {
      const number = Number(match[1])
      if (!citedRules.includes(number)) return { ok: false, reason: 'fremdeRegel', found: match[0] }
    }
    // Auch ausgeschrieben: "Das steht in Regel drei" nennt genauso eine Regel.
    for (const [words, number] of SPELLED) {
      const spelled = new RegExp(`\\b(?:Regel|Rule)\\s+(?:${words})\\b`, 'i')
      const hit = text.match(spelled)
      if (hit && !citedRules.includes(number)) return { ok: false, reason: 'fremdeRegel', found: hit[0] }
    }
    for (const match of text.matchAll(/\b(?:Anhang|Annex)\s+([IVX]+)\b/gi)) {
      const number = 100 + romanValue(match[1]!)
      if (!citedRules.includes(number)) return { ok: false, reason: 'fremdeRegel', found: match[0] }
    }
    const inland = text.match(/\b(Binnenfassung|Binnenregel\w*|inland)\b/i)
    if (inland && signal.scope !== 'inland') return { ok: false, reason: 'binnen', found: inland[0] }
    for (const judgement of JUDGEMENTS) {
      const hit = text.match(judgement)
      if (hit) return { ok: false, reason: 'wertung', found: hit[0] }
    }
  }
  return { ok: true }
}

const ROMAN: Record<string, number> = { i: 1, v: 5, x: 10 }

function romanValue(s: string): number {
  const digits = [...s.toLowerCase()].map((c) => ROMAN[c] ?? 0)
  return digits.reduce((sum, d, i) => sum + (digits.slice(i + 1).some((n) => n > d) ? -d : d), 0)
}
